import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import InfoOutlineRoundedIcon from '@mui/icons-material/InfoOutlineRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded'
import { getNlsString } from '../../nls/nlsRuntime'
import { Button } from './Button'
import { IconButton } from './IconButton'
import type { GeneratedReportRecord } from '../../types/report'
import type { PulseReport } from '../../types/pulseReport'
import { PULSE_REPORT_KIND_LABEL } from '../../types/pulseReport'
import { PulseBody } from '../Ai/PulseBody'
import { canAnnotateAttachmentPreviewItem } from './attachmentPreviewHelpers'

export type AttachmentPreviewKind =
  | 'timeline'
  | 'image'
  | 'audio'
  | 'video'
  | 'document'
  | 'spreadsheet'
  | 'link'
  | 'asset'
  | 'file'
  | 'chat'
  | 'form'
  | 'form_responses'
  | 'design'
  | 'gsheet'

export interface AttachmentPreviewItem {
  id: string
  kind: AttachmentPreviewKind
  label: string
  summary?: string
  text?: string
  mimeType?: string
  sizeLabel?: string
  fileId?: string
  url?: string
  downloadUrl?: string
  isUnavailable?: boolean
  unavailableReason?: string
  formId?: string
  formResponseIds?: string[]
  formResponseCount?: number
}

export type AttachmentDocItem = ({ docType?: 'attachment' } & AttachmentPreviewItem)
export type ReportDocItem = { docType: 'report'; id: string; label: string; record: GeneratedReportRecord }
export type PulseDocItem = { docType: 'pulse'; id: string; label: string; pulse: PulseReport }

export type DocViewerItem = AttachmentDocItem | ReportDocItem | PulseDocItem

interface DocumentViewerProps {
  open: boolean
  onClose: () => void
  item?: DocViewerItem | null
  items?: DocViewerItem[]
  activeId?: string | null
  onActiveChange?: (id: string) => void
  onAnnotate?: (item: AttachmentPreviewItem, sourceUrl: string) => void | Promise<void>
  annotateLabel?: string
  onCopyLink?: (item: DocViewerItem) => string
  onDownload?: (item: DocViewerItem) => void | Promise<void>
  onShare?: (item: DocViewerItem) => void | Promise<void>
  sidebar?: ReactNode | ((item: DocViewerItem) => ReactNode)
  defaultSidebarOpen?: boolean
}

type PreviewCapability = 'image' | 'audio' | 'video' | 'pdf' | 'text' | 'link' | 'report' | 'pulse' | 'form' | 'form_responses' | 'fallback'

const TEXT_EXTENSIONS = new Set(['txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'xml', 'html', 'css', 'js', 'jsx', 'ts', 'tsx', 'log', 'ics'])
const MAX_TEXT_PREVIEW_CHARS = 120_000

const extensionOf = (name: string) => name.split('.').pop()?.trim().toLowerCase() ?? ''

const isReportItem = (item: DocViewerItem): item is ReportDocItem => item.docType === 'report'
const isPulseItem = (item: DocViewerItem): item is PulseDocItem => item.docType === 'pulse'
const isAttachmentItem = (item: DocViewerItem): item is AttachmentDocItem => !item.docType || item.docType === 'attachment'

const attachmentKindLabel = (item: AttachmentPreviewItem) => {
  if (item.kind === 'timeline') return getNlsString('NLSCommonPreviewKindTimelineItem')
  if (item.kind === 'image') return getNlsString('NLSCommonPreviewKindImage')
  if (item.kind === 'audio') return getNlsString('NLSCommonPreviewKindAudio')
  if (item.kind === 'video') return getNlsString('NLSCommonPreviewKindVideo')
  if (item.kind === 'spreadsheet') return getNlsString('NLSCommonPreviewKindSpreadsheet')
  if (item.kind === 'document') return getNlsString('NLSCommonPreviewKindDocument')
  if (item.kind === 'link') return getNlsString('NLSCommonPreviewKindLink')
  if (item.kind === 'asset') return getNlsString('NLSCommonPreviewKindAsset')
  if (item.kind === 'form') return 'Form'
  if (item.kind === 'form_responses') return 'Form responses'
  if (item.kind === 'design') return 'Design'
  if (item.kind === 'gsheet') return 'Google Sheet'
  if (item.kind === 'chat') return 'Chat'
  return getNlsString('NLSCommonPreviewKindFile')
}

const itemKindLabel = (item: DocViewerItem): string => {
  if (isReportItem(item)) return 'Report'
  if (isPulseItem(item)) return `${PULSE_REPORT_KIND_LABEL[item.pulse.kind]} · Pulse`
  return attachmentKindLabel(item)
}

const itemSubMeta = (item: DocViewerItem): string | null => {
  if (isReportItem(item)) {
    const parts: string[] = []
    if (item.record.outputFormat) parts.push(item.record.outputFormat)
    parts.push(`v${item.record.version}`)
    return parts.join(' · ')
  }
  if (isPulseItem(item)) {
    return `${item.pulse.creditsCharged} Cr · ${new Date(item.pulse.createdAtMs).toLocaleDateString()}`
  }
  return [item.sizeLabel, item.mimeType].filter(Boolean).join(' / ') || null
}

const previewCapability = (item: DocViewerItem): PreviewCapability => {
  if (isReportItem(item)) {
    if (item.record.content) return 'report'
    if (item.record.artifact?.downloadUrl) {
      const url = item.record.artifact.downloadUrl.toLowerCase()
      if (url.includes('.pdf') || item.record.artifact.format === 'PDF') return 'pdf'
    }
    return 'report'
  }
  if (isPulseItem(item)) return 'pulse'
  const mime = (item.mimeType ?? '').toLowerCase()
  const ext = extensionOf(item.label)
  if (item.kind === 'form') return 'form'
  if (item.kind === 'form_responses') return 'form_responses'
  if (item.kind === 'image') return 'image'
  if (item.kind === 'audio') return 'audio'
  if (item.kind === 'video') return 'video'
  if (item.kind === 'link') return 'link'
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (
    mime.startsWith('text/')
    || TEXT_EXTENSIONS.has(ext)
    || item.kind === 'spreadsheet'
    || item.kind === 'document'
    || item.kind === 'gsheet'
    || item.kind === 'design'
    || item.kind === 'chat'
    || Boolean(item.text)
  ) return 'text'
  return 'fallback'
}

const supportsZoom = (capability: PreviewCapability) => capability === 'image' || capability === 'pdf'
const supportsFontSize = (capability: PreviewCapability) => capability === 'report' || capability === 'pulse' || capability === 'text'

const resolveAttachmentUrl = async (item: AttachmentDocItem): Promise<{ url: string | null; unavailable: boolean }> => {
  if (item.isUnavailable) return { url: null, unavailable: true }
  if (!item.fileId) return { url: item.url ?? null, unavailable: false }
  try {
    const { getStorageFileUrl } = await import('../../services/storageHubService')
    const url = await getStorageFileUrl(item.fileId)
    return { url, unavailable: false }
  } catch {
    return { url: item.url?.startsWith('blob:') ? item.url : null, unavailable: true }
  }
}

const FormPreviewCard = ({ item }: { item: AttachmentDocItem }) => {
  const lines = (item.text || '').split('\n').filter(Boolean)
  const fieldLines = lines.filter((l) => /^\d+\.\s/.test(l.trim()))
  const fieldCount = fieldLines.length
  return (
    <article className="attachmentPreviewTextPanel" style={{ padding: 24 }}>
      <header style={{ marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-strong)' }}>
          {item.label}
        </h3>
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>
          {fieldCount > 0 && <span>{fieldCount} field{fieldCount === 1 ? '' : 's'}</span>}
          {typeof item.formResponseCount === 'number' && (
            <span>{item.formResponseCount} submission{item.formResponseCount === 1 ? '' : 's'}</span>
          )}
        </div>
      </header>
      {item.text ? (
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, color: 'var(--text)' }}>
          {item.text}
        </pre>
      ) : (
        <p style={{ color: 'var(--muted)' }}>No form schema details available.</p>
      )}
    </article>
  )
}

type ParsedResponseRow = {
  id: string
  submittedByName: string
  submittedAt: string
  fields: Array<{ key: string; value: string }>
}

const FormResponsePreviewCard = ({ item }: { item: AttachmentDocItem }) => {
  const rows: ParsedResponseRow[] = useMemo(() => {
    const lines = (item.text || '').split('\n').filter(Boolean)
    const parsed: ParsedResponseRow[] = []
    lines.forEach((line, idx) => {
      if (line.includes('Submitter:') || line.includes('Response ')) {
        const parts = line.split('|').map((p) => p.trim())
        let submitter = ''
        let date = ''
        const fields: Array<{ key: string; value: string }> = []
        for (const part of parts) {
          if (part.startsWith('Submitter:')) {
            submitter = part.replace('Submitter:', '').trim()
          } else if (part.startsWith('Date:')) {
            date = part.replace('Date:', '').trim()
          } else if (part.startsWith('Data:')) {
            const dataStr = part.replace('Data:', '').trim()
            dataStr.split(',').slice(0, 3).forEach((kv) => {
              const [k, v] = kv.split(':').map((s) => s.trim())
              if (k && v) fields.push({ key: k, value: v })
            })
          } else if (part.includes(':') && !part.startsWith('Response')) {
            const [k, v] = part.split(':').map((s) => s.trim())
            if (k && v) fields.push({ key: k, value: v })
          }
        }
        if (submitter || date || fields.length) {
          parsed.push({
            id: `row-${idx}`,
            submittedByName: submitter || 'Unknown',
            submittedAt: date || '—',
            fields: fields.slice(0, 3),
          })
        }
      }
    })
    return parsed
  }, [item.text])

  const count = item.formResponseIds?.length ?? item.formResponseCount ?? rows.length

  return (
    <article className="attachmentPreviewTextPanel" style={{ padding: 24 }}>
      <header style={{ marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-strong)' }}>
          {item.label}
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }}>
          {count} response{count === 1 ? '' : 's'} selected
        </p>
      </header>
      {rows.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-strong)', color: 'var(--muted)' }}>
                <th style={{ padding: '8px 12px' }}>Submitter</th>
                <th style={{ padding: '8px 12px' }}>Submitted At</th>
                <th style={{ padding: '8px 12px' }}>Fields</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--text-strong)' }}>
                    {row.submittedByName}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>
                    {row.submittedAt}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text)' }}>
                    {row.fields.map((f) => `${f.key}: ${f.value}`).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : item.text ? (
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, color: 'var(--text)' }}>
          {item.text}
        </pre>
      ) : (
        <p style={{ color: 'var(--muted)' }}>No response details available.</p>
      )}
    </article>
  )
}

const AttachmentBody = ({
  item,
  sourceUrl,
  textPreview,
  textError,
  isUnavailable,
  zoom,
}: {
  item: AttachmentDocItem
  sourceUrl: string | null
  textPreview: string | null
  textError: string | null
  isUnavailable: boolean
  zoom: number
}) => {
  if (isUnavailable) {
    return (
      <div className="attachmentPreviewDialogFallback">
        <InsertDriveFileRoundedIcon />
        <strong>{item.label}</strong>
        <p>{item.unavailableReason || getNlsString('NLSCommonPreviewAttachmentUnavailable')}</p>
      </div>
    )
  }
  const capability = previewCapability(item)
  if (capability === 'form') {
    return <FormPreviewCard item={item} />
  }
  if (capability === 'form_responses') {
    return <FormResponsePreviewCard item={item} />
  }
  if (capability === 'image' && sourceUrl) {
    return <img className="attachmentPreviewDialogMedia" src={sourceUrl} alt={item.label} style={{ transform: `scale(${zoom})`, transformOrigin: 'center center', transition: 'transform 120ms ease' }} />
  }
  if (capability === 'audio' && sourceUrl) {
    return <audio className="attachmentPreviewDialogMedia" src={sourceUrl} controls />
  }
  if (capability === 'video' && sourceUrl) {
    return <video className="attachmentPreviewDialogMedia" src={sourceUrl} controls />
  }
  if (capability === 'pdf' && sourceUrl) {
    return <iframe className="attachmentPreviewDialogFrame" src={sourceUrl} title={item.label} style={{ zoom }} />
  }
  if (capability === 'text') {
    return (
      <div className="attachmentPreviewTextPanel">
        {textPreview ? <pre>{textPreview}</pre> : <p>{textError ?? getNlsString('NLSCommonPreviewPreparingText')}</p>}
      </div>
    )
  }
  return (
    <div className="attachmentPreviewDialogFallback">
      {item.kind === 'timeline' ? <SubdirectoryArrowRightRoundedIcon /> : item.kind === 'link' ? <LinkRoundedIcon /> : <InsertDriveFileRoundedIcon />}
      <strong>{item.label}</strong>
      <p>{item.summary || getNlsString('NLSCommonPreviewFormatUnavailable')}</p>
    </div>
  )
}

const GeneratedReportContentView = ({ content }: { content: NonNullable<GeneratedReportRecord['content']> }) => (
  <>
    <h2>{content.title}</h2>
    <p>{content.executiveSummary}</p>
    {content.sections.map((section) => <section key={section.title}><h3>{section.title}</h3><p>{section.body}</p>{section.bullets.length ? <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul> : null}</section>)}
  </>
)

const ReportRendererBody = ({ item, fontScale }: { item: ReportDocItem; fontScale: number }) => {
  const { record } = item
  if (!record.content) {
    if (record.artifact?.downloadUrl && (record.artifact.format === 'PDF' || record.artifact.downloadUrl.toLowerCase().includes('.pdf'))) {
      return <iframe className="attachmentPreviewDialogFrame" src={record.artifact.downloadUrl} title={item.label} style={{ zoom: fontScale }} />
    }
    return (
      <div className="attachmentPreviewDialogFallback">
        <DescriptionRoundedIcon />
        <strong>{item.label}</strong>
        <p>This report has no rendered content yet. Open or download the original artifact when it is available.</p>
        {record.artifact?.downloadUrl ? <a href={record.artifact.downloadUrl} target="_blank" rel="noreferrer">Open original</a> : null}
      </div>
    )
  }
  return (
    <article className="documentViewerDocPanel" style={{ fontSize: `${Math.round(14 * fontScale)}px` }}>
      <GeneratedReportContentView content={record.content} />
    </article>
  )
}

const PulseRendererBody = ({ item, fontScale }: { item: PulseDocItem; fontScale: number }) => (
  <article className="documentViewerDocPanel pulseReportReader" style={{ fontSize: `${Math.round(14 * fontScale)}px` }}>
    <div className="pulseReportReader__body">
      <PulseBody report={item.pulse} />
    </div>
  </article>
)

const MetadataSidebar = ({ item, sidebar }: { item: DocViewerItem; sidebar?: ReactNode | ((item: DocViewerItem) => ReactNode) }) => {
  if (sidebar) return <>{typeof sidebar === 'function' ? sidebar(item) : sidebar}</>
  if (isReportItem(item)) {
    const r = item.record
    const rows: Array<[string, string]> = []
    if (r.category) rows.push(['Category', r.category])
    rows.push(['Format', r.outputFormat ?? '—'])
    rows.push(['Version', `v${r.version}`])
    if (r.createdAt) rows.push(['Created', r.createdAt.toLocaleString()])
    if (r.destination) rows.push(['Destination', r.destination])
    if (r.sourceScopes?.length) rows.push(['Sources', `${r.sourceScopes.length} scope${r.sourceScopes.length === 1 ? '' : 's'}`])
    if (r.rootReportId && r.rootReportId !== r.id) rows.push(['Root', r.rootReportId.slice(0, 10)])
    return (
      <dl className="documentViewerMetaList">
        {rows.map(([label, value]) => (
          <div key={label} className="documentViewerMetaRow"><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
    )
  }
  if (isPulseItem(item)) {
    const p = item.pulse
    return (
      <dl className="documentViewerMetaList">
        <div className="documentViewerMetaRow"><dt>Kind</dt><dd>{PULSE_REPORT_KIND_LABEL[p.kind]}</dd></div>
        <div className="documentViewerMetaRow"><dt>Status</dt><dd>{p.status}</dd></div>
        <div className="documentViewerMetaRow"><dt>Credits</dt><dd>{p.creditsCharged}</dd></div>
        <div className="documentViewerMetaRow"><dt>Created</dt><dd>{new Date(p.createdAtMs).toLocaleString()}</dd></div>
        {p.cached ? <div className="documentViewerMetaRow"><dt>Cached</dt><dd>Yes</dd></div> : null}
      </dl>
    )
  }
  return (
    <dl className="documentViewerMetaList">
      <div className="documentViewerMetaRow"><dt>Kind</dt><dd>{attachmentKindLabel(item)}</dd></div>
      {item.mimeType ? <div className="documentViewerMetaRow"><dt>MIME</dt><dd>{item.mimeType}</dd></div> : null}
      {item.sizeLabel ? <div className="documentViewerMetaRow"><dt>Size</dt><dd>{item.sizeLabel}</dd></div> : null}
      {item.summary ? <div className="documentViewerMetaRow"><dt>Notes</dt><dd>{item.summary}</dd></div> : null}
    </dl>
  )
}

export const DocumentViewer = ({
  open,
  onClose,
  item,
  items,
  activeId,
  onActiveChange,
  onAnnotate,
  annotateLabel = getNlsString('NLSCommonActionMarkUpInDesign'),
  onCopyLink,
  onDownload,
  onShare,
  sidebar,
  defaultSidebarOpen = false,
}: DocumentViewerProps) => {
  const collection = items && items.length > 0 ? items : null
  const activeItem: DocViewerItem | null = useMemo(() => {
    if (collection) return collection.find((entry) => entry.id === activeId) ?? collection[0] ?? null
    return item ?? null
  }, [activeId, collection, item])

  const [asyncResolved, setAsyncResolved] = useState<{ id: string; url: string | null; unavailable: boolean } | null>(null)
  const [textResult, setTextResult] = useState<{ url: string; text?: string | null; error?: string | null } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [fontScale, setFontScale] = useState(1)
  const [sidebarOpen, setSidebarOpen] = useState(defaultSidebarOpen)

  // Reset zoom and font scale on item change during render
  const [prevActiveId, setPrevActiveId] = useState(activeItem?.id)
  if (prevActiveId !== activeItem?.id) {
    setPrevActiveId(activeItem?.id)
    setZoom(1)
    setFontScale(1)
  }

  // Derive resolved url and unavailable state synchronously where possible
  let resolvedUrl: string | null = null
  let resolvedUnavailable = false
  if (open && activeItem && isAttachmentItem(activeItem)) {
    if (activeItem.fileId) {
      if (asyncResolved && asyncResolved.id === activeItem.id) {
        resolvedUrl = asyncResolved.url
        resolvedUnavailable = asyncResolved.unavailable
      } else {
        resolvedUrl = null
        resolvedUnavailable = activeItem.isUnavailable === true
      }
    } else {
      resolvedUrl = activeItem.url ?? null
      resolvedUnavailable = activeItem.isUnavailable === true
    }
  }

  useEffect(() => {
    let cancelled = false
    if (!open || !activeItem || !isAttachmentItem(activeItem) || !activeItem.fileId) return () => { cancelled = true }
    void resolveAttachmentUrl(activeItem).then((result) => {
      if (!cancelled) {
        setAsyncResolved({ id: activeItem.id, url: result.url, unavailable: result.unavailable })
      }
    })
    return () => { cancelled = true }
  }, [activeItem, open])

  // Derive text preview and error from textResult when matching current resolvedUrl
  const textPreview = textResult?.url === resolvedUrl ? textResult.text ?? null : null
  const textError = textResult?.url === resolvedUrl ? textResult.error ?? null : null

  useEffect(() => {
    let cancelled = false
    if (!open || !activeItem || !isAttachmentItem(activeItem) || !resolvedUrl || resolvedUnavailable || previewCapability(activeItem) !== 'text') {
      return () => { cancelled = true }
    }
    void fetch(resolvedUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Text preview failed.')
        return response.text()
      })
      .then((text) => {
        if (!cancelled) {
          setTextResult({
            url: resolvedUrl,
            text: text.length > MAX_TEXT_PREVIEW_CHARS ? `${text.slice(0, MAX_TEXT_PREVIEW_CHARS)}\n\n[${getNlsString('NLSCommonPreviewTextTruncated')}]` : text,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTextResult({
            url: resolvedUrl,
            error: getNlsString('NLSCommonPreviewTextBlocked'),
          })
        }
      })
    return () => { cancelled = true }
  }, [activeItem, open, resolvedUnavailable, resolvedUrl])

  const goToIndex = useCallback((next: number) => {
    if (!collection) return
    const bounded = Math.max(0, Math.min(collection.length - 1, next))
    const target = collection[bounded]
    if (target && onActiveChange) onActiveChange(target.id)
  }, [collection, onActiveChange])

  const activeIndex = collection && activeItem ? collection.findIndex((entry) => entry.id === activeItem.id) : -1

  useEffect(() => {
    if (!open) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return }
      if (!collection) return
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); goToIndex(activeIndex - 1) }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); goToIndex(activeIndex + 1) }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [activeIndex, collection, goToIndex, onClose, open])

  if (!open || !activeItem) return null

  const capability = previewCapability(activeItem)
  const downloadHref = isAttachmentItem(activeItem)
    ? (activeItem.downloadUrl ?? resolvedUrl ?? undefined)
    : isReportItem(activeItem) ? activeItem.record.artifact?.downloadUrl ?? undefined
    : undefined
  const openHref = isAttachmentItem(activeItem) ? resolvedUrl ?? undefined
    : isReportItem(activeItem) ? activeItem.record.artifact?.downloadUrl ?? undefined
    : undefined
  const showAttachmentFooter = isAttachmentItem(activeItem) && resolvedUrl && !resolvedUnavailable
  const showAnnotate = isAttachmentItem(activeItem) && resolvedUrl && !resolvedUnavailable && onAnnotate && canAnnotateAttachmentPreviewItem(activeItem)

  const copyLink = () => {
    if (!onCopyLink) return
    const url = onCopyLink(activeItem)
    if (url) void navigator.clipboard?.writeText(url)
  }

  const share = async () => {
    if (onShare) { await onShare(activeItem); return }
    if (onCopyLink) {
      const url = onCopyLink(activeItem)
      if (url && navigator.share) { try { await navigator.share({ title: activeItem.label, url }) } catch { void navigator.clipboard?.writeText(url) } }
      else if (url) void navigator.clipboard?.writeText(url)
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && activeItem && (
        <motion.div
          className="attachmentPreviewDialogOverlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={onClose}
        >
          <motion.section
            className={`attachmentPreviewDialogCard documentViewerCard${sidebarOpen ? ' hasSidebar' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={getNlsString('NLSCommonAriaPreviewAttachment', { label: activeItem.label })}
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="attachmentPreviewDialogHeader documentViewerHeader">
              <div>
                <p>{itemKindLabel(activeItem)}</p>
                <h2>{activeItem.label}</h2>
                {itemSubMeta(activeItem) ? <span>{itemSubMeta(activeItem)}</span> : null}
              </div>
              <div className="documentViewerHeaderActions">
                {supportsZoom(capability) && (
                  <div className="documentViewerToolbarGroup" role="group" aria-label="Zoom">
                    <IconButton
                      variant="tertiary"
                      size="sm"
                      label="Zoom out"
                      icon={<ZoomOutRoundedIcon fontSize="small" />}
                      onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                    />
                    <span>{Math.round(zoom * 100)}%</span>
                    <IconButton
                      variant="tertiary"
                      size="sm"
                      label="Zoom in"
                      icon={<ZoomInRoundedIcon fontSize="small" />}
                      onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                    />
                    <IconButton
                      variant="tertiary"
                      size="sm"
                      label="Reset zoom"
                      icon={<RestartAltRoundedIcon fontSize="small" />}
                      onClick={() => setZoom(1)}
                    />
                  </div>
                )}
                {supportsFontSize(capability) && (
                  <div className="documentViewerToolbarGroup" role="group" aria-label="Font size">
                    <IconButton
                      variant="tertiary"
                      size="sm"
                      label="Smaller text"
                      icon={<ZoomOutRoundedIcon fontSize="small" />}
                      onClick={() => setFontScale((s) => Math.max(0.8, s - 0.1))}
                    />
                    <span>{Math.round(fontScale * 100)}%</span>
                    <IconButton
                      variant="tertiary"
                      size="sm"
                      label="Larger text"
                      icon={<ZoomInRoundedIcon fontSize="small" />}
                      onClick={() => setFontScale((s) => Math.min(1.6, s + 0.1))}
                    />
                  </div>
                )}
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={sidebarOpen ? 'Hide details' : 'Show details'}
                  pressed={sidebarOpen}
                  icon={<TuneRoundedIcon fontSize="small" />}
                  onClick={() => setSidebarOpen((v) => !v)}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label={getNlsString('NLSCommonAriaClosePreview')}
                  icon={<CloseRoundedIcon fontSize="small" />}
                  onClick={onClose}
                />
              </div>
            </header>
            <div className="documentViewerStage">
              {collection && collection.length > 1 && (
                <span className="documentViewerNav documentViewerNavPrev">
                  <IconButton
                    variant="secondary"
                    size="md"
                    label="Previous document"
                    icon={<ChevronLeftRoundedIcon />}
                    disabled={activeIndex <= 0}
                    onClick={() => goToIndex(activeIndex - 1)}
                  />
                </span>
              )}
              <div className="attachmentPreviewDialogBody documentViewerBody">
                {isAttachmentItem(activeItem) ? (
                  <AttachmentBody item={activeItem} sourceUrl={resolvedUrl} textPreview={textPreview} textError={textError} isUnavailable={resolvedUnavailable} zoom={zoom} />
                ) : isReportItem(activeItem) ? (
                  <ReportRendererBody item={activeItem} fontScale={fontScale} />
                ) : (
                  <PulseRendererBody item={activeItem} fontScale={fontScale} />
                )}
              </div>
              {collection && collection.length > 1 && (
                <span className="documentViewerNav documentViewerNavNext">
                  <IconButton
                    variant="secondary"
                    size="md"
                    label="Next document"
                    icon={<ChevronRightRoundedIcon />}
                    disabled={activeIndex >= collection.length - 1}
                    onClick={() => goToIndex(activeIndex + 1)}
                  />
                </span>
              )}
              {sidebarOpen && (
                <aside className="documentViewerSidebar" aria-label="Document details">
                  <header><InfoOutlineRoundedIcon fontSize="small" /><strong>Details</strong></header>
                  <MetadataSidebar item={activeItem} sidebar={sidebar} />
                </aside>
              )}
            </div>
            <footer className="attachmentPreviewDialogFooter documentViewerFooter">
              {collection && collection.length > 1 && (
                <span className="documentViewerCollectionCount">{activeIndex + 1} / {collection.length}</span>
              )}
              {showAnnotate ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PaletteRoundedIcon fontSize="small" />}
                  onClick={() => { if (resolvedUrl) void onAnnotate!(activeItem as AttachmentPreviewItem, resolvedUrl) }}
                >
                  {annotateLabel}
                </Button>
              ) : null}
              {onCopyLink ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<LinkRoundedIcon fontSize="small" />}
                  onClick={copyLink}
                >
                  Copy link
                </Button>
              ) : null}
              {(onShare || onCopyLink) ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<IosShareRoundedIcon fontSize="small" />}
                  onClick={() => void share()}
                >
                  Share
                </Button>
              ) : null}
              {onDownload ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<DownloadRoundedIcon fontSize="small" />}
                  onClick={() => void onDownload(activeItem)}
                >
                  {getNlsString('NLSCommonActionDownload')}
                </Button>
              ) : downloadHref ? (
                <a href={downloadHref} download className="zapBtn zapBtn--tertiary zapBtn--sm">
                  <span className="zapBtn__icon zapBtn__icon--lead" aria-hidden="true">
                    <DownloadRoundedIcon fontSize="small" />
                  </span>
                  {getNlsString('NLSCommonActionDownload')}
                </a>
              ) : null}
              {showAttachmentFooter && openHref ? (
                <a href={openHref} target="_blank" rel="noreferrer" className="zapBtn zapBtn--tertiary zapBtn--sm">
                  <span className="zapBtn__icon zapBtn__icon--lead" aria-hidden="true">
                    <OpenInNewRoundedIcon fontSize="small" />
                  </span>
                  {getNlsString('NLSCommonActionOpen')}
                </a>
              ) : null}
            </footer>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

export const AttachmentPreviewDialog = DocumentViewer
